import "@/lib/zodConfig";
import "@/styles/theme.css";
import "@/i18n";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  configureSession,
  getSessionSnapshot,
  sessionActions,
  subscribeSession,
} from "@/features/auth/session";
import { getCurrentPrincipal } from "@/lib/api/generated/nextsub";
import { queryClient } from "@/lib/api/queryClient";
import { hasRetainedData } from "@/lib/api/retainedAttempt";
import { watchRetainedOwner } from "@/lib/api/retainedOwner";
import { leaveGuardActive } from "@/lib/leaveGuards";

import { router } from "@/router";

configureSession({
  // Session-owned data only: public pages (e.g. a shared ranking) keep
  // their queries across sign-in, sign-out and session changes.
  clearCaches: () => {
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "public" });
    queryClient.getMutationCache().clear();
  },
  fetchMe: (accessToken) =>
    getCurrentPrincipal(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined),
});
void sessionActions.bootstrap();

// Retained create attempts are dropped when the session ends or the user changes.
watchRetainedOwner(() => getSessionSnapshot().userId, subscribeSession);

// A create whose outcome is unknown keeps its payload (e.g. a generated
// password) and idempotency key only in this tab's memory, whether or not its
// form is open: closing or reloading the tab would lose them, so ask first.
window.addEventListener("beforeunload", (event) => {
  if (!hasRetainedData()) return;
  event.preventDefault();
  // Older browsers need returnValue set to show the prompt.
  event.returnValue = "";
});

// A deploy replaced the hashed chunks this tab expects: reload once. The
// marker is a timestamp, so a failure right after that reload stops (no loop)
// while a later deploy in the same tab can reload again.
const CHUNK_RELOAD_KEY = "nextsub.chunk-reload";
const CHUNK_RELOAD_WINDOW_MS = 30_000;

function readChunkReload(): number {
  try {
    return Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeChunkReload(value: number) {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(value));
  } catch {
    /* storage unavailable: worst case we reload once more */
  }
}

window.addEventListener("vite:preloadError", (event) => {
  // Never reload on our own while the tab holds something a reload would
  // lose for good (a create whose outcome is unknown, unsaved edits, a
  // one-time secret): the route error UI offers the reload instead.
  if (hasRetainedData() || leaveGuardActive()) return;
  // Reloaded for this reason moments ago and still failing: stop, let the
  // route error UI show instead of looping.
  if (Date.now() - readChunkReload() < CHUNK_RELOAD_WINDOW_MS) return;
  writeChunkReload(Date.now());
  event.preventDefault();
  window.location.reload();
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
